// src/components/reports/components/reducers/homePageStatusReducer.js
//
// Fetches home-page UI state from the unified GET /getHomePageStatus endpoint
// (one Clients.findOne + one Users.findOne per request instead of two round-trips).
// Module-level deduplication flags ensure any number of components can call the
// exported thunks without triggering more than one network request per session.

import axios from "axios";

/* -------------------- INITIAL STATE -------------------- */

const INITIAL_STATE = {
  bannerOpen: false,
  reportingContentOpen: false,
  folderStatus: {},
  folderStatusLoaded: false,
};

/* -------------------- ACTION TYPES -------------------- */

const SET_HOME_PAGE_STATUS = "homePageStatus/SET_HOME_PAGE_STATUS";
const SET_FOLDER_STATUS = "homePageStatus/SET_FOLDER_STATUS";
const SET_FOLDER_STATUS_LOADED = "homePageStatus/SET_FOLDER_STATUS_LOADED";

/* -------------------- DEDUPLICATION FLAG -------------------- */

let homePageStatusLoading = false;
let homePageStatusLoaded = false;

/* -------------------- THUNKS -------------------- */

export function getHomePageStatus() {
  return async (dispatch) => {
    if (homePageStatusLoading || homePageStatusLoaded) return;
    homePageStatusLoading = true;
    try {
      const res = await axios.get(`${process.env.REACT_APP_BIGHORN_SERVER}/api/app/getHomePageStatus`, {
        headers: { ClientToken: localStorage.getItem("clientToken") },
      });
      dispatch({
        type: SET_HOME_PAGE_STATUS,
        payload: {
          bannerOpen: res.data.headerBannerOpen,
          reportingContentOpen: res.data.homepageAppOpen,
          folderStatus: res.data.homepageAppOpenStatus || {},
        },
      });
      dispatch({ type: SET_FOLDER_STATUS_LOADED });
      homePageStatusLoaded = true;
    } finally {
      homePageStatusLoading = false;
    }
  };
}

// Backwards-compatible aliases — components that call these individually will still
// work, and both will resolve from the single unified request above.
export const getBannerStatus = getHomePageStatus;
export const getHomePageAppStatus = getHomePageStatus;

// Call after a successful setHomePageAppStatus POST so local state stays in sync
// without needing to re-fetch.
export function updateFolderStatus(folderStatus) {
  return { type: SET_FOLDER_STATUS, payload: folderStatus };
}

// Allow forced re-fetch (e.g. after the user changes settings)
export function resetHomePageStatusCache() {
  homePageStatusLoaded = false;
}

/* -------------------- REDUCER -------------------- */

export default function homePageStatusReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case SET_HOME_PAGE_STATUS:
      return {
        ...state,
        bannerOpen: action.payload.bannerOpen,
        reportingContentOpen: action.payload.reportingContentOpen,
        folderStatus: action.payload.folderStatus,
      };
    case SET_FOLDER_STATUS:
      return { ...state, folderStatus: action.payload };
    case SET_FOLDER_STATUS_LOADED:
      return { ...state, folderStatusLoaded: true };
    default:
      return state;
  }
}
